import { InventoryMapper } from '../../../../common/inventory/mappers/InventoryMapper';
import { ProfileMapper } from '../../../../common/mappers/ProfileMapper';
import { SeverityMapper } from '../../../../common/mappers/SeverityMapper';
import { Preset } from '../../../../common/models/valueObjects/Preset';
import { JavaProviderMapper } from '../../../../common/reports/mappers/JavaProviderMapper';
import { ObjectStatsMapper } from '../../../../common/reports/mappers/ObjectStatsMapper';
import { CallLimiting } from '../../../../common/reports/models/CallLimiting';
import { Coverage } from '../../../../common/reports/models/Coverage';
import { DateUtils } from '../../../../common/utils/DateUtils';
import { GQLIdEncoder } from '../../../../common/utils/GQLIdEncoder';
import { ReportPrintData } from '../models/ReportPrintData';
import { InstancesCounters } from '../models/valueObjects/InstancesCounters';
import { Issue } from '../models/valueObjects/Issue';

export class ReportPrintMapper {
  private readonly gqlIdEncoder = new GQLIdEncoder();
  private readonly severityMapper = new SeverityMapper();
  private readonly inventoryMapper = new InventoryMapper();
  private readonly profileMapper = new ProfileMapper();
  private readonly objectStatsMapper = new ObjectStatsMapper();
  private readonly javaProvidersMapper = new JavaProviderMapper();

  mapGQLModel(data: any): ReportPrintData {
    const trace = data.trace ? new Preset(data.trace.name, data.trace.name, data.traceType) : null;
    const profile = data.profile ? this.profileMapper.mapGQLModel(data.profile) : null;
    const project = data.project ? new Preset(this.gqlIdEncoder.decode(data.project.id).toString(), data.project.name) : null;
    const createdAt = DateUtils.toMoment(data.createdAt);
    const completedAt = DateUtils.toMoment(data.completedAt);

    const issues = data.rules ? this.mapIssues(data.rules) : [];
    const inventory = this.inventoryMapper.mapGQLModel(data.inventory);
    const stats = this.objectStatsMapper.mapGQLModel(data.objectStats);
    const javaProviders = this.javaProvidersMapper.mapGQLModel(data.javaProviders);
    const coverage = data.coverageSummary ? new Coverage(data.coverageSummary.total, data.coverageSummary.covered, data.coverageSummary.depth ?? null) : null;
    const callLimiting = data.callLimiting
      ? new CallLimiting(
          data.callLimiting.callsLimited,
          data.callLimiting.maxCallsPerBucket ?? null,
          data.callLimiting.maxCallsTotal ?? null,
          data.callLimiting.numCallsKept ?? null,
          data.callLimiting.numCallsOverBucketLimit ?? null,
          data.callLimiting.numCallsOverTotalLimit ?? null,
          data.callLimiting.numCallsTotal ?? null,
          data.callLimiting.numBuckets ?? null,
          data.callLimiting.numFullBuckets ?? null
        )
      : null;
    const instancesCounters = this.mapInstancesCounters(data);

    return new ReportPrintData(
      this.gqlIdEncoder.decode(data.id),
      data.name,
      createdAt,
      completedAt,
      trace,
      project,
      profile,
      issues,
      inventory,
      stats,
      data.nonsensitiveSalt,
      coverage,
      callLimiting,
      instancesCounters,
      javaProviders
    );
  }

  private mapIssues(data: any) {
    if (!data || !data.edges) return [];
    return data.edges.map(({ node }: any) => new Issue(node.number, node.title, node.shortExplanation, this.severityMapper.mapSeverity(node.severity)));
  }

  private mapInstancesCounters(data: any) {
    // Instances counters are only requested on the drawer-based export. Ignore them if they are not in the response
    if (['allInstances', 'highInstances', 'mediumInstances', 'lowInstances', 'passedInstances'].some((k) => !data[k])) return null;

    return new InstancesCounters(
      data.allInstances.totalCount,
      data.highInstances.totalCount,
      data.mediumInstances.totalCount,
      data.lowInstances.totalCount,
      data.passedInstances.totalCount
    );
  }
}
